import { useState, useEffect } from 'react';
import { useError } from '../../context/ErrorContext';
import { whyChooseUsService } from '../../services/whyChooseUsService';
import { Save, X, Plus, Trash2, Edit3, ImageIcon } from 'lucide-react';
import { nanoid } from 'nanoid';
import { motion, AnimatePresence } from 'framer-motion';

const WhyChooseUsContentManager = ({ setEditingWhyChooseUs, updatePageLastUpdated }) => {
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showError } = useError();

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        const content = await whyChooseUsService.getWhyChooseUsContent();
        setPageContent(content);
      } catch (error) {
        showError('Failed to load Why Choose Us content: ' + error.message, 'error');
        setPageContent(null);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [showError]);

  const handleChange = (sectionName, index, field, value) => {
    setPageContent(prevContent => {
      const newContent = { ...prevContent };
      if (sectionName === 'features') {
        const updatedFeatures = [...newContent.features];
        updatedFeatures[index] = { ...updatedFeatures[index], [field]: value };
        newContent.features = updatedFeatures;
      } else {
        newContent[field] = value;
      }
      return newContent;
    });
  };

  const handleSaveContent = async () => {
    try {
      setLoading(true);
      const updatedContent = await whyChooseUsService.createOrUpdateWhyChooseUsContent(pageContent);
      setPageContent(updatedContent);
      updatePageLastUpdated('Why Choose Us', updatedContent.lastUpdated);
      showError('Why Choose Us page updated successfully!', 'success');
    } catch (error) {
      showError('Failed to save Why Choose Us content: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const addFeature = () => {
    setPageContent(prevContent => ({
      ...prevContent,
      features: [...prevContent.features, { id: nanoid(), title: '', description: '', icon: '' }],
    }));
  };

  const deleteFeature = (id) => {
    setPageContent(prevContent => ({
      ...prevContent,
      features: prevContent.features.filter(item => item.id !== id),
    }));
  };

  if (loading || !pageContent) {
    return <div className="flex items-center justify-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-xl">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-8 w-8 text-accent dark:text-accent mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-lg text-gray-700 dark:text-gray-300">Loading Why Choose Us...</p>
        </div>
      </div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
      className="bg-white text-gray-800 rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 max-full mx-auto my-4 sm:my-6 border border-gray-200 dark:bg-gray-900 dark:text-white dark:border-gray-700"
    >
      {/* Header with accent color */}
      <div className="flex justify-between items-center mb-6 sm:mb-8 border-b-2 pb-4 border-accent dark:border-gray-700">
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
          Edit Why Choose Us
        </h2>
        <button
          onClick={() => setEditingWhyChooseUs(false)}
          className="p-2 sm:p-3 rounded-full text-gray-500 hover:bg-gray-100 transition-colors duration-300 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          <X size={24} sm:size={28} />
        </button>
      </div>

      <div className="space-y-8 sm:space-y-12">
        {/* Hero Section */}
        <section className="bg-gray-50 p-4 sm:p-6 rounded-2xl shadow-inner border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
          <h3 className="text-lg sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-700 flex items-center dark:text-gray-300">
            <Edit3 size={20} sm:size={24} className="mr-2 sm:mr-3 text-accent" /> Hero Section
          </h3>
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2 dark:text-gray-400">Title</label>
              <input
                type="text"
                value={pageContent.title}
                onChange={(e) => handleChange(null, null, 'title', e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-lg sm:rounded-xl shadow-sm p-3 sm:p-4 text-gray-800 focus:ring-accent focus:border-accent transition duration-300 placeholder-gray-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-500"
                placeholder="Enter page title..."
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2 dark:text-gray-400">Description</label>
              <textarea
                rows="3"
                value={pageContent.subtitle}
                onChange={(e) => handleChange(null, null, 'subtitle', e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-lg sm:rounded-xl shadow-sm p-3 sm:p-4 text-gray-800 focus:ring-accent focus:border-accent transition duration-300 placeholder-gray-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-500"
                placeholder="Describe your service in a compelling way..."
              ></textarea>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="bg-gray-50 p-4 sm:p-6 rounded-2xl shadow-inner border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
          <h3 className="text-lg sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-700 flex items-center dark:text-gray-300">
            <ImageIcon size={20} sm:size={24} className="mr-2 sm:mr-3 text-accent" /> Key Features
          </h3>
          <AnimatePresence>
            {pageContent.features.map((feature, index) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -50, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="p-4 sm:p-6 mb-4 sm:mb-6 bg-white border border-gray-200 rounded-xl shadow-md relative dark:bg-gray-700 dark:border-gray-600"
              >
                <div className="flex justify-between items-start mb-3 sm:mb-4">
                  <span className="font-semibold text-base sm:text-xl text-gray-800 dark:text-white">Feature {index + 1}</span>
                  <button
                    onClick={() => deleteFeature(feature.id)}
                    className="p-1 sm:p-2 rounded-full text-red-500 hover:bg-red-50 transition-colors duration-200 dark:hover:bg-red-900/50"
                  >
                    <Trash2 size={20} sm:size={24} />
                  </button>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1 dark:text-gray-400">Title</label>
                    <input
                      type="text"
                      value={feature.title}
                      onChange={(e) => handleChange('features', index, 'title', e.target.value)}
                      className="w-full bg-gray-100 border-gray-200 rounded-lg shadow-sm p-3 text-gray-800 focus:ring-accent focus:border-accent transition duration-200 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1 dark:text-gray-400">Description</label>
                    <textarea
                      rows="3"
                      value={feature.description}
                      onChange={(e) => handleChange('features', index, 'description', e.target.value)}
                      className="w-full bg-gray-100 border-gray-200 rounded-lg shadow-sm p-3 text-gray-800 focus:ring-accent focus:border-accent transition duration-200 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    ></textarea>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          <button
            onClick={addFeature}
            className="w-full flex items-center justify-center px-4 py-3 bg-green-500 text-black rounded-xl hover:bg-green-600 transition duration-300 font-semibold shadow-md mt-4"
          >
            <Plus size={20} className="mr-2" /> Add New Feature
          </button>
        </section>
      </div>

      {/* Save Button */}
      <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
        <button
          onClick={() => setEditingWhyChooseUs(false)}
          className="flex items-center justify-center px-6 sm:px-8 py-3 bg-gray-300 text-gray-800 rounded-xl shadow-lg hover:bg-gray-400 transition-all duration-300 font-bold transform hover:scale-105"
        >
          <X size={20} className="mr-2 sm:mr-3" />
          Close
        </button>
        <button
          onClick={handleSaveContent}
          className="flex items-center justify-center px-6 sm:px-8 py-3 bg-accent text-white rounded-xl shadow-lg hover:bg-accent/80 transition-all duration-300 font-bold transform hover:scale-105"
          disabled={loading}
        >
          <Save size={20} className="mr-2 sm:mr-3" />
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </motion.div>
  );
};

export default WhyChooseUsContentManager;